import axios from 'axios';
import { z } from 'zod';
import { RateLimiter } from './retry';
import apiClient from './client';
import { API_ENDPOINTS } from './config';
import { logError } from './interceptors';

export type AnalyticsGranularity = 'hourly' | 'daily' | 'monthly';

export interface AnalyticsTimeRangeParams {
  start: string;
  end: string;
}

export interface AnalyticsCostsSummaryParams extends AnalyticsTimeRangeParams {
  granularity?: AnalyticsGranularity;
}

export interface AnalyticsEventsParams extends AnalyticsTimeRangeParams {
  page?: number;
  page_size?: number;
}

export interface AnalyticsTopConsumersParams extends AnalyticsTimeRangeParams {
  dimension?: string;
  metric?: string;
  top_n?: number;
}

export interface AnalyticsModelFilterParams extends AnalyticsTimeRangeParams {
  model?: string;
}

export interface AnalyticsGranularityFilterParams extends AnalyticsTimeRangeParams {
  granularity?: string;
  model?: string;
}

export type AnalyticsResponse = Record<string, unknown>;

const dateTimeSchema = z.string().refine(value => !Number.isNaN(Date.parse(value)), {
  message: 'Invalid datetime format',
});

const timeRangeBaseSchema = z.object({
  start: dateTimeSchema,
  end: dateTimeSchema,
});

const timeRangeSchema = timeRangeBaseSchema.refine(
  data => new Date(data.start).getTime() <= new Date(data.end).getTime(),
  'start must be before end'
);

const costsSummarySchema = timeRangeBaseSchema
  .extend({
    granularity: z.enum(['hourly', 'daily', 'monthly']).optional(),
  })
  .superRefine((data, ctx) => {
    if (new Date(data.start).getTime() > new Date(data.end).getTime()) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'start must be before end',
        path: ['start'],
      });
    }
  });

const eventsSchema = timeRangeBaseSchema
  .extend({
    page: z.coerce.number().int().positive().max(10000).optional(),
    page_size: z.coerce.number().int().positive().max(200).optional(),
  })
  .superRefine((data, ctx) => {
    if (new Date(data.start).getTime() > new Date(data.end).getTime()) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'start must be before end',
        path: ['start'],
      });
    }
  });

const topConsumersSchema = timeRangeBaseSchema
  .extend({
    dimension: z.string().min(1).optional(),
    metric: z.string().min(1).optional(),
    top_n: z.coerce.number().int().positive().max(200).optional(),
  })
  .superRefine((data, ctx) => {
    if (new Date(data.start).getTime() > new Date(data.end).getTime()) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'start must be before end',
        path: ['start'],
      });
    }
  });

const modelFilterSchema = timeRangeBaseSchema
  .extend({
    model: z.string().min(1).optional(),
  })
  .superRefine((data, ctx) => {
    if (new Date(data.start).getTime() > new Date(data.end).getTime()) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'start must be before end',
        path: ['start'],
      });
    }
  });

const granularityFilterSchema = timeRangeBaseSchema
  .extend({
    granularity: z.string().min(1).optional(),
    model: z.string().min(1).optional(),
  })
  .superRefine((data, ctx) => {
    if (new Date(data.start).getTime() > new Date(data.end).getTime()) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'start must be before end',
        path: ['start'],
      });
    }
  });

const cacheStore = new Map<string, { expiresAt: number; data: AnalyticsResponse }>();
const defaultCacheTtlMs = 60000;

const endpointCacheTtl: Record<string, number> = {
  [API_ENDPOINTS.ANALYTICS.COSTS_SUMMARY]: 120000,
  [API_ENDPOINTS.ANALYTICS.COSTS_BY_MODEL]: 120000,
  [API_ENDPOINTS.ANALYTICS.USAGE_BY_API_KEY]: 60000,
  [API_ENDPOINTS.ANALYTICS.USAGE_BY_USER]: 60000,
  [API_ENDPOINTS.ANALYTICS.USAGE_BY_ACTION]: 60000,
  [API_ENDPOINTS.ANALYTICS.USAGE_TOP_CONSUMERS]: 60000,
  [API_ENDPOINTS.ANALYTICS.LATENCY_SUMMARY]: 60000,
  [API_ENDPOINTS.ANALYTICS.ERRORS_BY_MODEL]: 60000,
  [API_ENDPOINTS.ANALYTICS.TOKENS_TRENDS]: 60000,
  [API_ENDPOINTS.ANALYTICS.DASHBOARD_SUMMARY]: 30000,
  [API_ENDPOINTS.ANALYTICS.EVENTS]: 15000,
};

const defaultLimiter = new RateLimiter(60, 60000);
const eventsLimiter = new RateLimiter(30, 60000);

const endpointLimiters: Record<string, RateLimiter> = {
  [API_ENDPOINTS.ANALYTICS.EVENTS]: eventsLimiter,
};

const sortObjectKeys = (value: unknown): unknown => {
  if (Array.isArray(value)) {
    return value.map(item => sortObjectKeys(item));
  }

  if (value && typeof value === 'object') {
    const record = value as Record<string, unknown>;
    const sorted: Record<string, unknown> = {};
    Object.keys(record)
      .sort()
      .forEach(key => {
        const entry = record[key];
        if (entry !== undefined) {
          sorted[key] = sortObjectKeys(entry);
        }
      });
    return sorted;
  }

  return value;
};

const stableStringify = (value: unknown): string => {
  return JSON.stringify(sortObjectKeys(value));
};

const getCacheKey = (endpoint: string, params: Record<string, unknown>): string => {
  return `${endpoint}:${stableStringify(params)}`;
};

const readCache = (key: string): AnalyticsResponse | null => {
  const cached = cacheStore.get(key);
  if (!cached) {
    return null;
  }

  if (cached.expiresAt < Date.now()) {
    cacheStore.delete(key);
    return null;
  }

  return cached.data;
};

const writeCache = (key: string, data: AnalyticsResponse, ttlMs: number): void => {
  cacheStore.set(key, {
    data,
    expiresAt: Date.now() + ttlMs,
  });
};

const logAnalyticsEvent = (payload: {
  endpoint: string;
  params: Record<string, unknown>;
  durationMs: number;
  success: boolean;
  status?: number;
  cacheHit?: boolean;
}): void => {
  const event = {
    ...payload,
    timestamp: new Date().toISOString(),
  };

  console.info('analytics.request', event);
};

const enforceRateLimit = (endpoint: string): void => {
  const limiter = endpointLimiters[endpoint] ?? defaultLimiter;
  if (!limiter.makeRequest()) {
    throw new Error(`Rate limit exceeded for ${endpoint}`);
  }
};

const resolveCacheTtl = (endpoint: string): number => {
  return endpointCacheTtl[endpoint] ?? defaultCacheTtlMs;
};

const fetchAnalytics = async <T extends AnalyticsResponse>(
  endpoint: string,
  params: Record<string, unknown>
): Promise<T> => {
  enforceRateLimit(endpoint);

  const cacheKey = getCacheKey(endpoint, params);
  const cached = readCache(cacheKey);
  if (cached) {
    logAnalyticsEvent({
      endpoint,
      params,
      durationMs: 0,
      success: true,
      cacheHit: true,
    });
    return cached as T;
  }

  const startTime = performance.now();
  try {
    const response = await apiClient.get<T>(endpoint, { params });
    const durationMs = performance.now() - startTime;
    logAnalyticsEvent({
      endpoint,
      params,
      durationMs,
      success: true,
      status: response.status,
    });
    writeCache(cacheKey, response.data, resolveCacheTtl(endpoint));
    return response.data;
  } catch (error: unknown) {
    const durationMs = performance.now() - startTime;
    logAnalyticsEvent({
      endpoint,
      params,
      durationMs,
      success: false,
    });
    if (axios.isAxiosError(error)) {
      logError(error, `analytics:${endpoint}`);
    }
    throw error;
  }
};

export const analyticsApi = {
  getCostsSummary: async (params: AnalyticsCostsSummaryParams): Promise<AnalyticsResponse> => {
    const parsed = costsSummarySchema.parse(params);
    return fetchAnalytics(API_ENDPOINTS.ANALYTICS.COSTS_SUMMARY, parsed);
  },
  getEvents: async (params: AnalyticsEventsParams): Promise<AnalyticsResponse> => {
    const parsed = eventsSchema.parse(params);
    return fetchAnalytics(API_ENDPOINTS.ANALYTICS.EVENTS, parsed);
  },
  getCostsByModel: async (params: AnalyticsTimeRangeParams): Promise<AnalyticsResponse> => {
    const parsed = timeRangeSchema.parse(params);
    return fetchAnalytics(API_ENDPOINTS.ANALYTICS.COSTS_BY_MODEL, parsed);
  },
  getUsageByApiKey: async (params: AnalyticsTimeRangeParams): Promise<AnalyticsResponse> => {
    const parsed = timeRangeSchema.parse(params);
    return fetchAnalytics(API_ENDPOINTS.ANALYTICS.USAGE_BY_API_KEY, parsed);
  },
  getUsageByUser: async (params: AnalyticsTimeRangeParams): Promise<AnalyticsResponse> => {
    const parsed = timeRangeSchema.parse(params);
    return fetchAnalytics(API_ENDPOINTS.ANALYTICS.USAGE_BY_USER, parsed);
  },
  getUsageByAction: async (params: AnalyticsTimeRangeParams): Promise<AnalyticsResponse> => {
    const parsed = timeRangeSchema.parse(params);
    return fetchAnalytics(API_ENDPOINTS.ANALYTICS.USAGE_BY_ACTION, parsed);
  },
  getUsageTopConsumers: async (
    params: AnalyticsTopConsumersParams
  ): Promise<AnalyticsResponse> => {
    const parsed = topConsumersSchema.parse(params);
    return fetchAnalytics(API_ENDPOINTS.ANALYTICS.USAGE_TOP_CONSUMERS, parsed);
  },
  getLatencySummary: async (params: AnalyticsGranularityFilterParams): Promise<AnalyticsResponse> => {
    const parsed = granularityFilterSchema.parse(params);
    return fetchAnalytics(API_ENDPOINTS.ANALYTICS.LATENCY_SUMMARY, parsed);
  },
  getErrorsByModel: async (params: AnalyticsModelFilterParams): Promise<AnalyticsResponse> => {
    const parsed = modelFilterSchema.parse(params);
    return fetchAnalytics(API_ENDPOINTS.ANALYTICS.ERRORS_BY_MODEL, parsed);
  },
  getTokensTrends: async (params: AnalyticsGranularityFilterParams): Promise<AnalyticsResponse> => {
    const parsed = granularityFilterSchema.parse(params);
    return fetchAnalytics(API_ENDPOINTS.ANALYTICS.TOKENS_TRENDS, parsed);
  },
  getDashboardSummary: async (params: AnalyticsTimeRangeParams): Promise<AnalyticsResponse> => {
    const parsed = timeRangeSchema.parse(params);
    return fetchAnalytics(API_ENDPOINTS.ANALYTICS.DASHBOARD_SUMMARY, parsed);
  },
  getResourceCounts: async (): Promise<AnalyticsResponse> => {
    return fetchAnalytics(API_ENDPOINTS.ANALYTICS.RESOURCE_COUNTS, {});
  },
  getHealthOverview: async (): Promise<AnalyticsResponse> => {
    return fetchAnalytics(API_ENDPOINTS.ANALYTICS.HEALTH_OVERVIEW, {});
  },
  getCreditsSummary: async (params: { window_hours?: number } = {}): Promise<AnalyticsResponse> => {
    return fetchAnalytics(API_ENDPOINTS.ANALYTICS.CREDITS_SUMMARY, params);
  },
  getCreditsBySource: async (params: AnalyticsTimeRangeParams): Promise<AnalyticsResponse> => {
    const parsed = timeRangeSchema.parse(params);
    return fetchAnalytics(API_ENDPOINTS.ANALYTICS.CREDITS_BY_SOURCE, parsed);
  },
  getTopAgents: async (params: AnalyticsTimeRangeParams & { top_n?: number }): Promise<AnalyticsResponse> => {
    return fetchAnalytics(API_ENDPOINTS.ANALYTICS.AGENTS_TOP, params as unknown as Record<string, unknown>);
  },
  getTopTools: async (params: AnalyticsTimeRangeParams & { top_n?: number }): Promise<AnalyticsResponse> => {
    return fetchAnalytics(API_ENDPOINTS.ANALYTICS.TOOLS_TOP, params as unknown as Record<string, unknown>);
  },
  getExecutionsRate: async (params: AnalyticsTimeRangeParams & { granularity?: string }): Promise<AnalyticsResponse> => {
    return fetchAnalytics(API_ENDPOINTS.ANALYTICS.EXECUTIONS_RATE, params as unknown as Record<string, unknown>);
  },
};

export const analyticsOpenApiSpec: Record<string, unknown> = {
  openapi: '3.0.3',
  info: {
    title: 'A2C Analytics Proxy',
    version: '1.0.0',
  },
  paths: {
    [API_ENDPOINTS.ANALYTICS.COSTS_SUMMARY]: {
      get: {
        summary: 'Cost summary',
        parameters: [
          {
            name: 'start',
            in: 'query',
            required: true,
            schema: { type: 'string', format: 'date-time' },
          },
          {
            name: 'end',
            in: 'query',
            required: true,
            schema: { type: 'string', format: 'date-time' },
          },
          {
            name: 'granularity',
            in: 'query',
            required: false,
            schema: { type: 'string', enum: ['hourly', 'daily', 'monthly'] },
          },
        ],
        responses: {
          '200': {
            description: 'Cost summary response',
            content: {
              'application/json': {
                schema: {
                  type: 'object',
                  additionalProperties: true,
                },
              },
            },
          },
        },
      },
    },
    [API_ENDPOINTS.ANALYTICS.EVENTS]: {
      get: {
        summary: 'Usage events',
        parameters: [
          {
            name: 'start',
            in: 'query',
            required: true,
            schema: { type: 'string', format: 'date-time' },
          },
          {
            name: 'end',
            in: 'query',
            required: true,
            schema: { type: 'string', format: 'date-time' },
          },
          {
            name: 'page',
            in: 'query',
            required: false,
            schema: { type: 'integer', minimum: 1 },
          },
          {
            name: 'page_size',
            in: 'query',
            required: false,
            schema: { type: 'integer', minimum: 1, maximum: 200 },
          },
        ],
        responses: {
          '200': {
            description: 'Paginated events',
            content: {
              'application/json': {
                schema: {
                  type: 'object',
                  additionalProperties: true,
                },
              },
            },
          },
        },
      },
    },
    [API_ENDPOINTS.ANALYTICS.COSTS_BY_MODEL]: {
      get: {
        summary: 'Costs by model',
        parameters: [
          {
            name: 'start',
            in: 'query',
            required: true,
            schema: { type: 'string', format: 'date-time' },
          },
          {
            name: 'end',
            in: 'query',
            required: true,
            schema: { type: 'string', format: 'date-time' },
          },
        ],
        responses: {
          '200': {
            description: 'Cost breakdown by model',
            content: {
              'application/json': {
                schema: {
                  type: 'object',
                  additionalProperties: true,
                },
              },
            },
          },
        },
      },
    },
    [API_ENDPOINTS.ANALYTICS.USAGE_BY_API_KEY]: {
      get: {
        summary: 'Usage by API key',
        parameters: [
          {
            name: 'start',
            in: 'query',
            required: true,
            schema: { type: 'string', format: 'date-time' },
          },
          {
            name: 'end',
            in: 'query',
            required: true,
            schema: { type: 'string', format: 'date-time' },
          },
        ],
        responses: {
          '200': {
            description: 'Usage by API key',
            content: {
              'application/json': {
                schema: { type: 'object', additionalProperties: true },
              },
            },
          },
        },
      },
    },
    [API_ENDPOINTS.ANALYTICS.USAGE_BY_USER]: {
      get: {
        summary: 'Usage by user',
        parameters: [
          {
            name: 'start',
            in: 'query',
            required: true,
            schema: { type: 'string', format: 'date-time' },
          },
          {
            name: 'end',
            in: 'query',
            required: true,
            schema: { type: 'string', format: 'date-time' },
          },
        ],
        responses: {
          '200': {
            description: 'Usage by user',
            content: {
              'application/json': {
                schema: { type: 'object', additionalProperties: true },
              },
            },
          },
        },
      },
    },
    [API_ENDPOINTS.ANALYTICS.USAGE_BY_ACTION]: {
      get: {
        summary: 'Usage by action',
        parameters: [
          {
            name: 'start',
            in: 'query',
            required: true,
            schema: { type: 'string', format: 'date-time' },
          },
          {
            name: 'end',
            in: 'query',
            required: true,
            schema: { type: 'string', format: 'date-time' },
          },
        ],
        responses: {
          '200': {
            description: 'Usage by action',
            content: {
              'application/json': {
                schema: { type: 'object', additionalProperties: true },
              },
            },
          },
        },
      },
    },
    [API_ENDPOINTS.ANALYTICS.USAGE_TOP_CONSUMERS]: {
      get: {
        summary: 'Top consumers',
        parameters: [
          {
            name: 'start',
            in: 'query',
            required: true,
            schema: { type: 'string', format: 'date-time' },
          },
          {
            name: 'end',
            in: 'query',
            required: true,
            schema: { type: 'string', format: 'date-time' },
          },
          {
            name: 'dimension',
            in: 'query',
            required: false,
            schema: { type: 'string' },
          },
          {
            name: 'metric',
            in: 'query',
            required: false,
            schema: { type: 'string' },
          },
          {
            name: 'top_n',
            in: 'query',
            required: false,
            schema: { type: 'integer', minimum: 1, maximum: 200 },
          },
        ],
        responses: {
          '200': {
            description: 'Top consumer list',
            content: {
              'application/json': {
                schema: { type: 'object', additionalProperties: true },
              },
            },
          },
        },
      },
    },
    [API_ENDPOINTS.ANALYTICS.LATENCY_SUMMARY]: {
      get: {
        summary: 'Latency summary',
        parameters: [
          {
            name: 'start',
            in: 'query',
            required: true,
            schema: { type: 'string', format: 'date-time' },
          },
          {
            name: 'end',
            in: 'query',
            required: true,
            schema: { type: 'string', format: 'date-time' },
          },
          {
            name: 'granularity',
            in: 'query',
            required: false,
            schema: { type: 'string' },
          },
          {
            name: 'model',
            in: 'query',
            required: false,
            schema: { type: 'string' },
          },
        ],
        responses: {
          '200': {
            description: 'Latency summary response',
            content: {
              'application/json': {
                schema: { type: 'object', additionalProperties: true },
              },
            },
          },
        },
      },
    },
    [API_ENDPOINTS.ANALYTICS.ERRORS_BY_MODEL]: {
      get: {
        summary: 'Errors by model',
        parameters: [
          {
            name: 'start',
            in: 'query',
            required: true,
            schema: { type: 'string', format: 'date-time' },
          },
          {
            name: 'end',
            in: 'query',
            required: true,
            schema: { type: 'string', format: 'date-time' },
          },
          {
            name: 'model',
            in: 'query',
            required: false,
            schema: { type: 'string' },
          },
        ],
        responses: {
          '200': {
            description: 'Error rates by model',
            content: {
              'application/json': {
                schema: { type: 'object', additionalProperties: true },
              },
            },
          },
        },
      },
    },
    [API_ENDPOINTS.ANALYTICS.TOKENS_TRENDS]: {
      get: {
        summary: 'Token trends',
        parameters: [
          {
            name: 'start',
            in: 'query',
            required: true,
            schema: { type: 'string', format: 'date-time' },
          },
          {
            name: 'end',
            in: 'query',
            required: true,
            schema: { type: 'string', format: 'date-time' },
          },
          {
            name: 'granularity',
            in: 'query',
            required: false,
            schema: { type: 'string' },
          },
          {
            name: 'model',
            in: 'query',
            required: false,
            schema: { type: 'string' },
          },
        ],
        responses: {
          '200': {
            description: 'Token trends response',
            content: {
              'application/json': {
                schema: { type: 'object', additionalProperties: true },
              },
            },
          },
        },
      },
    },
    [API_ENDPOINTS.ANALYTICS.DASHBOARD_SUMMARY]: {
      get: {
        summary: 'Dashboard summary',
        parameters: [
          {
            name: 'start',
            in: 'query',
            required: true,
            schema: { type: 'string', format: 'date-time' },
          },
          {
            name: 'end',
            in: 'query',
            required: true,
            schema: { type: 'string', format: 'date-time' },
          },
        ],
        responses: {
          '200': {
            description: 'Dashboard summary response',
            content: {
              'application/json': {
                schema: { type: 'object', additionalProperties: true },
              },
            },
          },
        },
      },
    },
  },
};
